/**
 * Valut-admin-only "Visualizar como" — client-side capability overlay.
 * Stored in sessionStorage; does not create users or change server roles.
 */

import { BAAS_GROUP_ROLE_PERMISSIONS } from "../../../Services/baasUsersApi";
import {
  BAAS_SCOPE_PRESET_CORRENTISTA,
  BAAS_SCOPE_PRESET_ISSUER,
  BAAS_SCOPE_PRESETS,
} from "./Instances/baasInstanceCreateConstants";

const STORAGE_KEY = "baas.viewAs.persona";

/** @typedef {'admin'|'operator'|'viewer'|'issuer'|'correntista'} BaasViewAsPersonaId */

export const BAAS_VIEW_AS_PERSONAS = [
  { id: "admin", role: "admin" },
  { id: "operator", role: "operator" },
  { id: "viewer", role: "viewer" },
  { id: "issuer", role: "operator", productPreset: BAAS_SCOPE_PRESET_ISSUER },
  { id: "correntista", role: "operator", productPreset: BAAS_SCOPE_PRESET_CORRENTISTA },
];

export const readBaasViewAsPersona = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const id = String(raw).trim();
    return BAAS_VIEW_AS_PERSONAS.find((p) => p.id === id) || null;
  } catch {
    return null;
  }
};

export const setBaasViewAsPersona = (personaId) => {
  try {
    if (!personaId) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, String(personaId));
  } catch {
    // ignore
  }
};

export const isBaasViewAsActive = () => Boolean(readBaasViewAsPersona());

export const clearBaasViewAsPersona = () => setBaasViewAsPersona(null);

const ISSUER_PRODUCTS = {
  issuer: { enabled: true },
  cards: { enabled: true },
  entities: { enabled: true },
  api: { enabled: true },
  credit: { enabled: true },
  pix: { enabled: false },
  stables: { enabled: false },
  fiat: { enabled: false },
  efx: { enabled: false },
};

const CORRENTISTA_PRODUCTS = {
  issuer: { enabled: false },
  cards: { enabled: false },
  entities: { enabled: true },
  api: { enabled: true },
  credit: { enabled: false },
  pix: { enabled: true },
  stables: { enabled: true },
  fiat: { enabled: true },
  efx: { enabled: true },
};

/**
 * Overlay real capabilities with a persona preview.
 * @param {object} capabilities
 * @param {{ id: string, role: string, productPreset?: string } | null} persona
 */
export const applyBaasViewAsOverlay = (capabilities, persona) => {
  if (!capabilities || !persona) return capabilities;

  const portalPermissions =
    BAAS_GROUP_ROLE_PERMISSIONS[persona.role] ?? BAAS_GROUP_ROLE_PERMISSIONS.viewer;

  // Expand portal codes the same way baasInstanceCapabilitiesApi does for evaluateCapability.
  const CONSOLE_TO_CAPABILITY = {
    "console.read": [
      "console.read",
      "entities.read",
      "accounts.read",
      "issuer.read",
      "credit.read",
      "transactions.read",
      "stables.read",
    ],
    "console.write": [
      "console.write",
      "entities.create",
      "issuer.programs.create",
      "accounts.create",
      "instances.create",
    ],
    "api.manage": ["api.keys.manage"],
    "users.manage": ["users.manage"],
    "transactions.read": ["transactions.read", "stables.read"],
    "transactions.write": ["transactions.write", "stables.operate"],
    "reports.read": ["reports.read"],
  };

  const permissions = [];
  for (const raw of portalPermissions) {
    const mapped = CONSOLE_TO_CAPABILITY[raw];
    if (mapped) permissions.push(...mapped);
    else permissions.push(raw);
  }

  let products = { ...(capabilities.products || {}) };
  if (persona.productPreset === BAAS_SCOPE_PRESET_ISSUER) {
    products = { ...products, ...ISSUER_PRODUCTS };
  } else if (persona.productPreset === BAAS_SCOPE_PRESET_CORRENTISTA) {
    products = { ...products, ...CORRENTISTA_PRODUCTS };
  }

  // Ensure preset scopes exist for documentation / future scope gates.
  void BAAS_SCOPE_PRESETS;

  return {
    ...capabilities,
    role: persona.role,
    permissions: [...new Set(permissions)],
    products,
    viewAsPersonaId: persona.id,
  };
};
